import { useState, useEffect } from 'react'
import { createClientComponentClient } from '@/integrations/supabase/client'

export type SponsorTier = 'standard' | 'elite' | 'bundle'
export type SponsorInterval = 'month' | 'six_months' | 'year'

export interface TierPricing {
  month: number
  six_months: number
  year: number
}

export interface SponsorPricing {
  standard: TierPricing
  elite: TierPricing
  bundle: TierPricing
}

export function useSponsorPricing(): SponsorPricing {
  const [pricing, setPricing] = useState<SponsorPricing>({
    standard: { month: 17.50, six_months: 94.50, year: 168 },
    elite:    { month: 22.50, six_months: 121.50, year: 216 },
    bundle:   { month: 29.50, six_months: 159,    year: 282.50 },
  })

  useEffect(() => {
    const supabase = createClientComponentClient()
    supabase.from('subscription_plans')
      .select('plan_key, price_monthly, price_six_monthly, price_yearly')
      .in('plan_key', [
        'sponsor_seat_monthly', 'sponsor_elite_seat', 'sponsor_bundle_seat',
        'corporate_edu_pack', 'corporate_elite_pack', 'corporate_bundle_pack', 'pay_it_forward'
      ])
      // Prioritize primary sponsor seat plans over legacy aliases
      .order('plan_key', { ascending: false })
      .then(({ data }) => {
        if (!data?.length) return   // keep fallbacks on DB hiccup
        setPricing(prev => {
          const next = {
            standard: { ...prev.standard },
            elite: { ...prev.elite },
            bundle: { ...prev.bundle }
          }
          // Sort so canonical keys ('sponsor_*') execute last, taking precedence over legacy alias rows
          const sorted = [...data].sort((a, b) => {
            const isCanonA = a.plan_key?.startsWith('sponsor_') ? 1 : 0
            const isCanonB = b.plan_key?.startsWith('sponsor_') ? 1 : 0
            return isCanonA - isCanonB
          })

          for (const row of sorted) {
            const m = Number(row.price_monthly)
            const s = Number(row.price_six_monthly)
            const y = Number(row.price_yearly)

            const updateTier = (tier: keyof SponsorPricing) => {
              if (m > 0) next[tier].month = m
              if (s > 0) next[tier].six_months = s
              if (y > 0) next[tier].year = y
            }

            if (row.plan_key === 'sponsor_seat_monthly' || row.plan_key === 'corporate_edu_pack' || row.plan_key === 'pay_it_forward') {
              updateTier('standard')
            }
            if (row.plan_key === 'sponsor_elite_seat' || row.plan_key === 'corporate_elite_pack') {
              updateTier('elite')
            }
            if (row.plan_key === 'sponsor_bundle_seat' || row.plan_key === 'corporate_bundle_pack') {
              updateTier('bundle')
            }
          }
          return next
        })
      })
  }, [])

  return pricing
}
